import { useEffect } from 'react'

import { SYSTEM_DARK_QUERY, THEME_STORAGE_KEY } from '@/lib/theme'
import { reportTheme } from '@/lib/themeTelemetry'
import useThemeStore from '@/store/themeStore'

/**
 * Mount once, from _app.tsx. Reads the stored preference after hydration and keeps the
 * page in step with the OS setting and with the same app open in another tab.
 */
export function useThemeListeners(): void {
  useEffect(() => {
    useThemeStore.getState().hydrate()

    // Report the hydrated theme once, then only real changes (a pick, the OS flipping
    // under 'system', another tab). Subscribing after hydrate keeps the initial report
    // from being sent twice.
    const { preference, resolved } = useThemeStore.getState()
    reportTheme(preference, resolved)
    const unsubscribe = useThemeStore.subscribe((state, prev) => {
      if (state.preference !== prev.preference || state.resolved !== prev.resolved) {
        reportTheme(state.preference, state.resolved)
      }
    })

    const media = window.matchMedia(SYSTEM_DARK_QUERY)
    const onSystemChange = (e: MediaQueryListEvent | { matches: boolean }) => {
      useThemeStore.getState().syncFromSystem(e.matches)
    }
    media.addEventListener('change', onSystemChange as (e: MediaQueryListEvent) => void)

    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY) useThemeStore.getState().hydrate()
    }
    window.addEventListener('storage', onStorage)

    return () => {
      unsubscribe()
      media.removeEventListener('change', onSystemChange as (e: MediaQueryListEvent) => void)
      window.removeEventListener('storage', onStorage)
    }
  }, [])
}

export default function useTheme() {
  const preference = useThemeStore((s) => s.preference)
  const resolved = useThemeStore((s) => s.resolved)
  const setPreference = useThemeStore((s) => s.setPreference)
  return { preference, resolved, setPreference }
}
